import type { MedicationPadItemType } from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.types';
import type { ProductFormulation } from '@/constants/products';
import BigNumber from 'bignumber.js';
import { MAX_DAILY_DOSE_LABEL } from './constants';
import { formatWithUnit } from './formatWithUnit';
import { getFormulationFilter } from './getFormulationFilter';
import { getFormulationUnit } from './getFormulationUnit';
import { padItemSupportsMaxDailyDose } from './supportsMaxDailyDose';

/**
 * The maximum daily dose phrase with the formulation's unit, e.g. 'Maximum daily dose 3 g'; undefined without a dose
 * greater than 0. `parseMaxDailyDose` reads it back only when the formulation has a unit. Whether the item gets a
 * phrase at all is the caller's decision.
 */
export const getMaxDailyDosePhrase = (
  maxDailyDose: number | undefined,
  formulation: ProductFormulation | undefined,
) => {
  if (maxDailyDose === undefined || !Number.isFinite(maxDailyDose) || maxDailyDose <= 0) {
    return undefined;
  }
  // Plain decimal notation, never `1e+23`, which the parser cannot read back on reissue
  return `${MAX_DAILY_DOSE_LABEL} ${formatWithUnit(BigNumber(maxDailyDose).toFixed(), getFormulationUnit(formulation))}`;
};

/** `getMaxDailyDosePhrase` for a pad item, undefined when its formulation has no maximum daily dose. */
export const getPadItemMaxDailyDosePhrase = (item: MedicationPadItemType) =>
  padItemSupportsMaxDailyDose(item)
    ? getMaxDailyDosePhrase(item.medicationPadItem?.maxDailyDose, getFormulationFilter(item.filters))
    : undefined;
